export type Bilingual = {
  en: string
  fr: string
}

export type DifferenceIconId =
  | 'identity'
  | 'systems'
  | 'intelligence'
  | 'operations'

export const differenceIntro = {
  eyebrow: {
    en: 'The difference',
    fr: 'La différence',
  },
  headlineLead: {
    en: 'Not a collection of features.',
    fr: 'Pas un simple assemblage de fonctionnalités.',
  },
  headlineBefore: {
    en: 'An ',
    fr: 'Une ',
  },
  headlineAccent: {
    en: 'infrastructure',
    fr: 'infrastructure',
  },
  headlineAfter: {
    en: ' that connects them.',
    fr: ' qui les relie.',
  },
  intro: {
    en: 'Identity, context, systems, intelligence and action all run on one foundation.',
    fr: 'Identité, contexte, systèmes, intelligence et action fonctionnent sur une même fondation.',
  },
}

export type Differentiator = {
  num: string
  icon: DifferenceIconId
  label: Bilingual
  headlineLead: Bilingual
  headlineMuted: Bilingual
  body: Bilingual
  footer: Bilingual
}

export const differentiators: Differentiator[] = [
  {
    num: '01',
    icon: 'identity',
    label: { en: 'Open identity', fr: 'Identité ouverte' },
    headlineLead: {
      en: 'An ecosystem identity.',
      fr: 'Une identité d’écosystème.',
    },
    headlineMuted: {
      en: 'Not a portal account.',
      fr: 'Pas un compte de portail.',
    },
    body: {
      en: 'A persistent identity can follow a person across the website, the app and connected services — with relationships, rights and consents.',
      fr: 'Une identité persistante peut suivre la personne entre le site, l’application et les services connectés — avec relations, droits et consentements.',
    },
    footer: {
      en: 'OIDC · SSO · Relationships · Rights · Consents',
      fr: 'OIDC · SSO · Relations · Droits · Consentements',
    },
  },
  {
    num: '02',
    icon: 'systems',
    label: { en: 'Connected systems', fr: 'Systèmes connectés' },
    headlineLead: {
      en: 'Systems that work together.',
      fr: 'Des systèmes qui travaillent ensemble.',
    },
    headlineMuted: {
      en: 'Not just side by side.',
      fr: 'Pas seulement côte à côte.',
    },
    body: {
      en: 'BCITI | ATNA can connect data, events and actions to existing systems to create end-to-end journeys.',
      fr: 'BCITI | ATNA peut relier données, événements et actions aux systèmes existants pour créer des parcours de bout en bout.',
    },
    footer: {
      en: 'APIs · Connectors · Data · Transactions',
      fr: 'API · Connecteurs · Données · Transactions',
    },
  },
  {
    num: '03',
    icon: 'intelligence',
    label: { en: 'Verified intelligence', fr: 'Intelligence vérifiée' },
    headlineLead: {
      en: 'Intelligence verifies before it acts.',
      fr: 'L’intelligence vérifie avant d’agir.',
    },
    headlineMuted: { en: '', fr: '' },
    body: {
      en: 'ATNA combines AI understanding, data and deterministic rules, verification and confidence levels before recommending or executing an action.',
      fr: 'ATNA combine compréhension par IA, données et règles déterministes, vérification et niveaux de confiance avant de recommander ou d’exécuter une action.',
    },
    footer: {
      en: 'Understand · Verify · Authorize · Act',
      fr: 'Comprendre · Vérifier · Autoriser · Agir',
    },
  },
  {
    num: '04',
    icon: 'operations',
    label: { en: 'One operational view', fr: 'Vue opérationnelle unifiée' },
    headlineLead: {
      en: 'One view to understand and act.',
      fr: 'Une seule vue pour comprendre et agir.',
    },
    headlineMuted: { en: '', fr: '' },
    body: {
      en: 'Services, requests, communications and operations brought together in one decision layer.',
      fr: 'Services, requêtes, communications et opérations réunis dans une même couche décisionnelle.',
    },
    footer: {
      en: 'Services · Performance · SLA · Analytics',
      fr: 'Services · Performance · Niveaux de service · Analytique',
    },
  },
]

export const differenceFlow: {
  icon: DifferenceIconId
  title: Bilingual
  caption: Bilingual
}[] = [
  {
    icon: 'identity',
    title: { en: 'Open identity', fr: 'Identité ouverte' },
    caption: { en: 'Trusted identity', fr: 'Identité de confiance' },
  },
  {
    icon: 'systems',
    title: { en: 'Connected systems', fr: 'Systèmes connectés' },
    caption: { en: 'Unified data', fr: 'Données unifiées' },
  },
  {
    icon: 'intelligence',
    title: { en: 'Verified intelligence', fr: 'Intelligence vérifiée' },
    caption: {
      en: 'From insight to action',
      fr: 'De l’intelligence à l’action',
    },
  },
  {
    icon: 'operations',
    title: { en: 'Trusted action', fr: 'Action de confiance' },
    caption: { en: 'Real outcomes', fr: 'Résultats concrets' },
  },
]

export const differenceConclusion = {
  brandSuffix: {
    en: 'connects it all.',
    fr: 'fait le lien.',
  },
  caption: {
    en: 'One foundation. Real possibilities.',
    fr: 'Une même fondation. De vraies possibilités.',
  },
}
